import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPosts } from '../lib/store';
import { formatDate } from '../lib/utils';
import BookPromo from '../components/BookPromo';
import SafeIcon from '../common/SafeIcon';
import { FiClock, FiChevronRight, FiFolder, FiRefreshCw } from 'react-icons/fi';

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Wir rufen die Daten direkt ab. getPosts nutzt nun die 'posts' Tabelle.
      const data = await getPosts(true);
      if (data && data.length > 0) {
        setPosts(data);
      } else {
        setPosts([]);
      }
    } catch (err) {
      setError("Beiträge konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <>
      <Helmet>
        <title>Dividenden Investor | Finanzwissen für den DACH-Raum</title>
        <meta name="description" content="Unabhängiges Wissen für Investoren in Deutschland, Österreich und der Schweiz. Fokus auf Cashflow, Qualität und langfristigen Vermögensaufbau." />
      </Helmet>

      <section className="mb-16 text-center max-w-3xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-extrabold text-finance-blue mb-4 tracking-tight">
          Intelligent investieren.<br/>
          <span className="text-finance-green">Dividenden kassieren.</span>
        </h2>
        <p className="text-lg text-slate-600">
          Tiefgehende Analysen, erprobte Strategien und steuerliches Know-how für den erfolgreichen Vermögensaufbau in Deutschland, Österreich und der Schweiz.
        </p>
      </section>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-finance-green mb-4"></div>
          <p className="text-slate-500 font-medium italic">Lade frische Daten aus Supabase...</p>
        </div>
      ) : error ? (
        <div className="text-center py-20 bg-red-50 rounded-xl border border-red-100 p-8">
          <p className="text-red-600 font-semibold mb-4">{error}</p>
          <button 
            onClick={loadData}
            className="inline-flex items-center gap-2 bg-finance-blue text-white px-6 py-2 rounded-lg hover:bg-slate-800 transition"
          >
            <SafeIcon icon={FiRefreshCw} /> Erneut versuchen
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {posts.map((post) => (
            <article 
              key={post.id} 
              className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow group flex flex-col"
            >
              <div className="p-6 flex-grow flex flex-col">
                <div className="flex items-center gap-4 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">
                  <span className="flex items-center gap-1 text-finance-green bg-emerald-50 px-2 py-1 rounded">
                    <SafeIcon icon={FiFolder} />
                    {post.category}
                  </span>
                  <span>{formatDate(post.date)}</span>
                </div>
                
                <h3 className="text-2xl font-bold text-finance-blue mb-3 group-hover:text-finance-green transition-colors">
                  <Link to={`/post/${post.slug}`}>
                    {post.title}
                  </Link>
                </h3>
                
                <p className="text-slate-600 mb-6 flex-grow line-clamp-3">
                  {post.description}
                </p>
                
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-1.5 text-sm text-slate-500">
                    <SafeIcon icon={FiClock} />
                    <span>{post.read_time || '5'} Min. Lesezeit</span>
                  </div>
                  <Link 
                    to={`/post/${post.slug}`} 
                    className="flex items-center gap-1 text-sm font-semibold text-finance-blue group-hover:text-finance-green transition-colors"
                  >
                    Weiterlesen <SafeIcon icon={FiChevronRight} />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {(!loading && !error && posts.length === 0) && (
        <div className="text-center py-20 bg-slate-50 rounded-xl border border-dashed border-slate-300 mb-16">
          <p className="text-slate-500 mb-4">Noch keine veröffentlichten Beiträge in der Tabelle 'posts' gefunden.</p>
          <Link to="/admin" className="text-finance-green font-bold hover:underline">
            Zum Admin-Bereich & ersten Post erstellen
          </Link>
        </div>
      )}

      <BookPromo variant="full" />
    </>
  );
};

export default Home;